import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import EmptyState from '../../components/shell/EmptyState'
import StatusChip, { type RecordState } from '../../components/agent/StatusChip'
import { supabase } from '../../lib/supabase'
import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'
import { useRefetchOnFocus } from '../../lib/useRefetchOnFocus'
import { PAY_PERIOD, PAY_PERIOD_DATES } from './payPeriod19'
import { PAY_PERIOD_20_DATES } from './payPeriod20'
import { getMyPeriodState, getMyScheduleDays, unscheduledDay } from './agentScheduleStore'
import type { DayEntry } from '../../components/agent/scheduleDay'
import { getLeaveRequests, leaveRequestDisplayState, type LeaveRequest } from './leaveRequestStore'
import { getPendingDraft, submitPendingDraft, type PendingDraft } from './pendingDraftStore'
import { getMyScheduleSubmissions, submitSchedule, type MyScheduleSubmission } from './scheduleSubmissionStore'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import ScheduleAcknowledgements from '../../components/agent/ScheduleAcknowledgements'
import { schedulingServiceEnabled } from '../../lib/features'

// The actual current date, the same as AgentCalendarPage.tsx's own
// REAL_TODAY_ISO: the Today and Next shift cards track the real date. Read
// once at load; nothing here needs to tick live.
const now = new Date()
const REAL_TODAY_ISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

const TODAY_LABEL = `Today · ${now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`

// The two periods the agent's own schedule is read across: the current one
// and the next, so a next shift past the current period's last day still
// finds one.
const SCHEDULE_DATES = [...PAY_PERIOD_DATES, ...PAY_PERIOD_20_DATES]

function parseISO(date: string): Date {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function formatDateShort(date: string): string {
  return parseISO(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// A single day reads as just that day, not a same-to-same range.
function formatRequestDetail(request: LeaveRequest): string {
  const start = formatDateShort(request.startDate)
  return request.startDate === request.endDate ? start : `${start}–${formatDateShort(request.endDate)}`
}

// Same derivation as AgentCalendarPage.tsx's own formatRange: the draft's
// date range was never stored as its own field, only as the days it holds.
function formatDraftRange(days: PendingDraft['days']): string {
  const first = days[0]
  const last = days[days.length - 1]
  const firstLabel = `${first.month.slice(0, 3)} ${first.day}`
  const lastLabel = first.month === last.month ? `${last.day}` : `${last.month.slice(0, 3)} ${last.day}`
  return `${firstLabel}–${lastLabel}`
}

/**
 * Placeholder content standing in for the real schedule, until the schema,
 * roles, and the pay-period data behind this screen exist (see
 * docs/product/delivery-order.md). The shape matches
 * docs/product/scheduling-model.md and docs/design/journey-map.md's agent
 * dashboard spec, so swapping in real records is a data change, not a
 * layout change.
 */
function SectionLabel({ children }: { children: string }) {
  return <h2 className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{children}</h2>
}

function CheckCircleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <path d="m22 4-10 10-3-3" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

export default function AgentDashboardPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { format } = useTimeFormat()
  const identity = useProfileIdentity()

  // Captured once, from the state the copy-a-schedule screen's Save Draft
  // action navigated here with: a plain page reload has no such state, only
  // arriving from that screen does. Cleared from history right after so a
  // later reload of this same page does not resurface it.
  const [savedDraftPeriod] = useState<string | null>(
    (location.state as { draftSaved?: boolean; periodLabel?: string } | null)?.draftSaved
      ? ((location.state as { periodLabel?: string }).periodLabel ?? null)
      : null,
  )
  const [showSavedBanner, setShowSavedBanner] = useState(savedDraftPeriod !== null)

  // Same pattern as the saved-draft banner above, for the leave-request
  // screen's own submission instead.
  const leaveState = location.state as { leaveRequested?: boolean; leaveCancelled?: boolean } | null
  const [leaveCancelled] = useState<boolean>(Boolean(leaveState?.leaveCancelled))
  const [leaveRequested] = useState<boolean>(Boolean(leaveState?.leaveRequested) || leaveCancelled)
  const [showLeaveBanner, setShowLeaveBanner] = useState(leaveRequested)

  // Same pattern again, for Submit on the copy screen's saved-draft view:
  // that path navigates away on success, unlike this page's own in-place
  // Submit button below, which needs no banner since the chip flipping to
  // Submitted right there is confirmation enough.
  const [submittedDraftPeriod] = useState<string | null>(
    (location.state as { draftSubmitted?: boolean; periodLabel?: string } | null)?.draftSubmitted
      ? ((location.state as { periodLabel?: string }).periodLabel ?? null)
      : null,
  )
  const [showSubmittedBanner, setShowSubmittedBanner] = useState(submittedDraftPeriod !== null)

  const [userId, setUserId] = useState<string | null>(null)
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([])
  const [pendingDraft, setPendingDraft] = useState<PendingDraft | null>(null)
  // The real record, read unconditionally rather than gated on a local
  // pendingDraft existing or matching: sessionStorage does not survive a
  // new device or a cleared browser, but this does, the same reason
  // leaveRequests above never depends on anything local either.
  const [submissions, setSubmissions] = useState<MyScheduleSubmission[]>([])
  const [submitDraftFailed, setSubmitDraftFailed] = useState(false)
  // The server's reason for a refused submit, when it gave one.
  const [submitDraftMessage, setSubmitDraftMessage] = useState<string | null>(null)
  // The agent's own real schedule across the current and next periods
  // (unscheduled where no entry covers a day), and the current period's
  // state. Starts unscheduled so the cards render before the read lands.
  const [scheduleDays, setScheduleDays] = useState<DayEntry[]>(() => SCHEDULE_DATES.map(unscheduledDay))
  const [periodState, setPeriodState] = useState<RecordState | null>(null)
  // Distinguishes scheduleDays' own initial all-unscheduled state from a
  // real read that came back the same way: Build your current schedule
  // below only ever belongs on the latter.
  const [daysLoaded, setDaysLoaded] = useState(false)

  useEffect(() => {
    // Runs again once state clears to null below, and does nothing that
    // second time: this only ever needs to fire for the state the page was
    // entered with.
    if (location.state) navigate('.', { replace: true, state: null })
  }, [location.state, navigate])

  // Reloads the agent's own real records: called on mount, and again
  // whenever the tab regains focus (useRefetchOnFocus below), since
  // nothing here subscribes to realtime changes. A supervisor deciding a
  // request in another tab, or on another device, would otherwise leave
  // this page showing exactly what it fetched at mount for as long as it
  // stays open. Kicks the read off rather than awaiting it directly, the
  // same shape the effect below used inline before this was pulled out
  // to also serve useRefetchOnFocus: every actual setState call still
  // happens inside the `.then` callback, not this function's own body.
  function loadRealData(): void {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const id = session?.user.id ?? null
      setUserId(id)
      if (id) {
        const requests = await getLeaveRequests(id)
        setLeaveRequests(requests)
        setPendingDraft(getPendingDraft(id))
        const loadedSubmissions = await getMyScheduleSubmissions(id)
        // A submission never goes away once made, so an empty read is a
        // failed one: keep the last real state rather than replacing a
        // decision with nothing.
        if (loadedSubmissions.length > 0) setSubmissions(loadedSubmissions)
        const loadedDays = await getMyScheduleDays(id, SCHEDULE_DATES)
        setScheduleDays(loadedDays)
        setDaysLoaded(true)
        setPeriodState(await getMyPeriodState(id, PAY_PERIOD_DATES[0].date, loadedDays.slice(0, PAY_PERIOD_DATES.length)))
      }
    })
  }

  useEffect(() => {
    loadRealData()
  }, [])

  useRefetchOnFocus(loadRealData)

  // Submits in place, right from the list, rather than sending the agent
  // back into the copy screen just to press one more button: the draft
  // itself does not change here, only what happens to it next. Writes to
  // the real schedule_entries and schedule_submissions tables
  // (submitSchedule) before marking the local draft submitted, the same
  // order AgentCopySchedulePage.tsx's own handleSubmitSavedDraft follows.
  async function handleSubmitDraft() {
    if (!userId || !pendingDraft) {
      setSubmitDraftFailed(true)
      return
    }
    const submitted = await submitSchedule(pendingDraft.periodStart, pendingDraft.days)
    if (!submitted.ok) {
      setSubmitDraftMessage(submitted.message)
      setSubmitDraftFailed(true)
      return
    }
    if (!submitPendingDraft(userId)) {
      setSubmitDraftFailed(true)
      return
    }
    setSubmitDraftFailed(false)
    setSubmitDraftMessage(null)
    setPendingDraft(getPendingDraft(userId))
    setSubmissions(await getMyScheduleSubmissions(userId))
  }

  // The agent's own submitted leave requests and saved schedule draft
  // first, ahead of the placeholder example standing in for everyone
  // else's, so what an agent just did shows up where they will actually
  // look for it.
  // Today's own entry, and the next day actually carrying a work shift, not
  // simply the next calendar day: an off day, training day, or leave day
  // right after today is not "the next scheduled shift"
  // docs/design/journey-map.md's dashboard spec asks for. The label drops
  // the date when there is none to show, rather than fabricate one.
  const todayEntry = scheduleDays.find((entry) => entry.date === REAL_TODAY_ISO && entry.category !== 'unscheduled')
  const nextShiftEntry = scheduleDays.find((entry) => entry.date > REAL_TODAY_ISO && entry.category === 'work')
  const nextShiftLabel = nextShiftEntry
    ? `Next shift · ${nextShiftEntry.weekday}, ${nextShiftEntry.month.slice(0, 3)} ${nextShiftEntry.day}`
    : 'Next shift'

  // Build your current schedule only ever belongs here: none of the
  // agent's own schedule entries cover any day of the current period yet,
  // the same real read AgentCopySchedulePage.tsx's own session effect
  // checks to decide it should build that period instead of the next one.
  const currentPeriodEmpty =
    daysLoaded && !scheduleDays.slice(0, PAY_PERIOD_DATES.length).some((entry) => entry.category !== 'unscheduled')

  const displayedRequests: {
    title: string
    detail: string
    state: RecordState
    action?: { label: string; onClick: () => void }
  }[] = [
    ...leaveRequests
      .filter((request) => request.state === 'pending')
      .map((request) => ({
        title: request.leaveType,
        detail: formatRequestDetail(request),
        state: leaveRequestDisplayState(request.state),
      })),
    // A still-local, never-submitted draft always shows its own row with
    // Submit: pendingDraftStore.ts's own PendingDraft is the only source
    // for that, since nothing has reached schedule_submissions yet.
    // Otherwise the real submission wins over anything local, since it is
    // what a supervisor actually sees and decides. A local draft already
    // marked submitted is never shown on its own: it never learns of a
    // decision, so it would keep reading Submitted after one.
    ...(pendingDraft && pendingDraft.state === 'draft'
      ? [
          {
            title: `${pendingDraft.periodLabel} schedule`,
            detail: formatDraftRange(pendingDraft.days),
            state: 'draft' as RecordState,
            action: { label: 'Submit', onClick: handleSubmitDraft },
          },
        ]
      : []),
    // Every submission still awaiting a decision, one per pay period; a
    // decided one moves to recentDecisions below instead.
    ...submissions
      .filter((submission) => submission.state === 'submitted')
      .map((submission) => ({
        title: `${submission.periodLabel} schedule`,
        detail: submission.dateRange,
        state: submission.state as RecordState,
      })),
  ]

  // A real request leaves Pending requests the moment a supervisor decides
  // it and lands here, newest leave decision first.
  const recentDecisions: { title: string; comment?: string; supervisor: string; state: RecordState; editHref?: string }[] = [
    ...leaveRequests
      .filter((request) => request.state !== 'pending')
      .sort((a, b) => (b.decidedAt ?? '').localeCompare(a.decidedAt ?? ''))
      .map((request) => ({
        title: `${request.leaveType} · ${formatRequestDetail(request)}`,
        comment: request.decisionComment,
        supervisor: request.decidedByName ?? 'Your supervisor',
        state: leaveRequestDisplayState(request.state),
        // A returned request is the agent's to change and send again.
        editHref: request.state === 'returned' ? `/agent/request-leave?reopen=${request.id}` : undefined,
      })),
    ...submissions
      .filter((submission) => submission.state !== 'submitted')
      .map((submission) => ({
        title: `${submission.periodLabel} schedule`,
        comment: submission.decisionComment,
        supervisor: submission.decidedByName ?? 'Your supervisor',
        state: submission.state as RecordState,
        editHref: submission.state === 'returned' ? `/agent/copy?reopen=${submission.id}` : undefined,
      })),
  ]

  return (
    <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
      <a
        href="#agent-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="agent-main" tabIndex={0} className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 lg:gap-6 lg:p-8">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <h1 className="text-h3 font-extrabold lg:text-h2">{identity ? `${identity.name} Dashboard` : 'Dashboard'}</h1>
            <p className="mt-0.5 text-small text-ink-muted">
              {PAY_PERIOD.label} &middot; {PAY_PERIOD.range}
            </p>
          </div>
          {periodState && <StatusChip state={periodState} />}
        </div>

        {schedulingServiceEnabled() && <ScheduleAcknowledgements />}

        {showSavedBanner && savedDraftPeriod && (
          <div role="status" className="flex items-center gap-3 rounded-surface border border-success/30 bg-success/10 p-3">
            <span className="flex-none text-success">
              <CheckCircleIcon />
            </span>
            <p className="flex-1 text-small font-bold">{savedDraftPeriod} draft saved.</p>
            <button
              type="button"
              onClick={() => setShowSavedBanner(false)}
              aria-label="Dismiss"
              className="flex size-11 flex-none items-center justify-center text-ink-muted"
            >
              <CloseIcon />
            </button>
          </div>
        )}

        {showLeaveBanner && (
          <div role="status" className="flex items-center gap-3 rounded-surface border border-success/30 bg-success/10 p-3">
            <span className="flex-none text-success">
              <CheckCircleIcon />
            </span>
            <p className="flex-1 text-small font-bold">{leaveCancelled ? 'Leave request cancelled.' : 'Leave request submitted.'}</p>
            <button
              type="button"
              onClick={() => setShowLeaveBanner(false)}
              aria-label="Dismiss"
              className="flex size-11 flex-none items-center justify-center text-ink-muted"
            >
              <CloseIcon />
            </button>
          </div>
        )}

        {showSubmittedBanner && submittedDraftPeriod && (
          <div role="status" className="flex items-center gap-3 rounded-surface border border-success/30 bg-success/10 p-3">
            <span className="flex-none text-success">
              <CheckCircleIcon />
            </span>
            <p className="flex-1 text-small font-bold">{submittedDraftPeriod} submitted.</p>
            <button
              type="button"
              onClick={() => setShowSubmittedBanner(false)}
              aria-label="Dismiss"
              className="flex size-11 flex-none items-center justify-center text-ink-muted"
            >
              <CloseIcon />
            </button>
          </div>
        )}

        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-6">
          <div className="rounded-surface bg-surface-2 p-3.5 lg:p-5">
            <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{TODAY_LABEL}</p>
            <p className="mt-2 text-h2 font-extrabold [font-variant-numeric:tabular-nums]">
              {todayEntry ? formatScheduleLabel(todayEntry.label, format) : 'Nothing scheduled'}
            </p>
            {todayEntry?.detail && <p className="mt-1 text-small text-ink-muted">{todayEntry.detail}</p>}
          </div>

          <div className="rounded-surface border border-line p-3.5 lg:p-5">
            <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{nextShiftLabel}</p>
            <div className="mt-1.5 flex items-baseline justify-between lg:mt-2">
              <span className="text-h4 font-bold [font-variant-numeric:tabular-nums]">
                {nextShiftEntry ? formatScheduleLabel(nextShiftEntry.label, format) : 'Nothing scheduled'}
              </span>
              {nextShiftEntry?.detail && <span className="text-small text-ink-muted">{nextShiftEntry.detail}</span>}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start gap-2">
          <Link
            to="/agent/schedule-template"
            className="flex min-h-11 items-center gap-1.5 self-start text-small font-bold text-primary"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 3" />
            </svg>
            View your normal schedule
          </Link>

          {currentPeriodEmpty && (
            <Link
              to="/agent/copy?period=current"
              className="flex min-h-11 items-center gap-1.5 self-start text-small font-bold text-primary"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="12" height="12" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Build your current schedule
            </Link>
          )}

          {schedulingServiceEnabled() && (
            <>
            <Link
              to="/agent/swaps"
              className="flex min-h-11 items-center gap-1.5 self-start text-small font-bold text-primary"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 7h13l-3-3M17 17H4l3 3" />
              </svg>
              Swap a shift
            </Link>

            <Link
              to="/agent/open-shifts"
              className="flex min-h-11 items-center gap-1.5 self-start text-small font-bold text-primary"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v8M8 12h8" />
              </svg>
              View open shifts
            </Link>

            <Link
              to="/agent/availability"
              className="flex min-h-11 items-center gap-1.5 self-start text-small font-bold text-primary"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="17" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18M9 15l2 2 4-4" />
              </svg>
              Set your availability
            </Link>
            </>
          )}

          <Link
            to="/agent/request-history"
            className="flex min-h-11 items-center gap-1.5 self-start text-small font-bold text-primary"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 6h16M4 12h16M4 18h10" />
            </svg>
            View request history
          </Link>
        </div>

        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
          <section aria-labelledby="pending-requests">
            <div id="pending-requests">
              <SectionLabel>Pending requests</SectionLabel>
            </div>
            {submitDraftFailed && (
              <p role="alert" className="mt-2 text-caption font-semibold text-error">
                {submitDraftMessage
                  ? `Could not update the schedule. ${submitDraftMessage}.`
                  : 'Could not update the schedule. Storage may be unavailable in this browser; try again or use a different browser.'}
              </p>
            )}
            {displayedRequests.length === 0 ? (
              <EmptyState>No pending requests. Submit a leave request or a schedule change to see it here.</EmptyState>
            ) : (
              <ul className="mt-2 flex flex-col gap-2">
                {displayedRequests.map((request, index) => (
                  <li
                    key={`${request.title}-${index}`}
                    className="flex items-center justify-between gap-3 rounded-surface border border-line p-3"
                  >
                    <div>
                      <p className="text-small font-bold">{request.title}</p>
                      <p className="mt-0.5 text-small text-ink-muted">{request.detail}</p>
                    </div>
                    <div className="flex flex-none items-center gap-2">
                      {request.action && (
                        <button
                          type="button"
                          onClick={request.action.onClick}
                          className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-2.5 text-caption font-bold hover:bg-surface-2"
                        >
                          {request.action.label}
                        </button>
                      )}
                      <StatusChip state={request.state} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="recent-decisions">
            <div id="recent-decisions">
              <SectionLabel>Recent decisions</SectionLabel>
            </div>
            {recentDecisions.length === 0 ? (
              <EmptyState>No decisions yet. Your supervisor&apos;s decisions on submitted requests appear here.</EmptyState>
            ) : (
              <ul className="mt-2 flex flex-col gap-2">
                {recentDecisions.map((decision, index) => (
                  <li key={`${decision.title}-${index}`} className="rounded-surface border border-line p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-small font-bold">{decision.title}</p>
                      <StatusChip state={decision.state} />
                    </div>
                    <p className="mt-1.5 text-small text-ink-muted">
                      {decision.comment ? (
                        <>
                          {decision.supervisor}: &ldquo;{decision.comment}&rdquo;
                        </>
                      ) : (
                        decision.supervisor
                      )}
                    </p>
                    {decision.editHref && (
                      <Link
                        to={decision.editHref}
                        aria-label={`Edit and resubmit ${decision.title}`}
                        className="mt-1 flex min-h-11 items-center self-start text-small font-bold text-primary underline underline-offset-2"
                      >
                        Edit and resubmit
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>

      <div className="flex flex-none flex-col gap-2 border-t border-line p-4 pb-[max(1rem,env(safe-area-inset-bottom))] lg:flex-row lg:p-6">
        <Link
          to="/agent/copy?period=next"
          className="flex items-center justify-center gap-2 rounded-control bg-primary px-4 py-3.5 text-small font-bold text-primary-ink hover:bg-primary-hover lg:flex-1"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="9" y="9" width="12" height="12" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          Build Your Next Pay Period
        </Link>
        <div className="flex gap-2 lg:flex-1">
          <Link
            to="/agent/request-leave"
            className="flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-control border border-line-strong text-caption font-semibold"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" />
            </svg>
            Request leave
          </Link>
          <Link
            to="/agent/calendar"
            className="flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-control border border-line-strong text-caption font-semibold"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            View calendar
          </Link>
        </div>
      </div>
    </div>
  )
}
